/* Parcours detail page — Festival edition */
const ParcoursDetailPage = ({ slug }) => {
  const D = window.PA_DATA;
  const p = D.parcours.find(x => x.slug === slug) || D.parcours.find(x => x.status !== "past") || D.parcours[0];
  const artists = D.artists.filter(a => p.artistIds.includes(a.id));
  const venues  = D.venues.filter(v => p.venueIds.includes(v.id));
  const documents = Array.isArray(p.documents) ? p.documents : [];
  const hasDocuments = documents.length > 0;
  const [tab, setTab] = useState("programme"); // programme | documents | artistes | lieux
  const [focusedVenue, setFocusedVenue] = useState(null);

  const program = Array.isArray(p.programSillons) ? p.programSillons : D.programSillons;

  // "Sur la carte" (fiche lieu) : bascule sur l'onglet Lieux (qui contient la
  // carte), centre la vue sur le lieu, puis scrolle jusqu'à la carte.
  useEffect(() => {
    const onVenueFocus = (e) => {
      setTab("lieux");
      setFocusedVenue(e.detail);
      setTimeout(() => {
        document.getElementById("parcours-map")?.scrollIntoView({behavior:"smooth", block:"start"});
      }, 150);
    };
    window.addEventListener("focus-venue", onVenueFocus);
    return () => window.removeEventListener("focus-venue", onVenueFocus);
  }, []);

  useEffect(() => {
    if (tab === "documents" && !hasDocuments) {
      setTab("programme");
    }
  }, [tab, hasDocuments]);

  return (
    <div data-screen-label="03 Parcours — Détail" style={{paddingBottom:80}}>

      {/* ===== HERO ===== */}
      <DetailHero p={p}/>

      {/* ===== TABS ===== */}
      <section id="programme" data-parcours-tabs className="container-w" style={{paddingTop:48, paddingBottom:16, scrollMarginTop:80}}>
        <div className="row gap-7 wrap" style={{borderBottom:"2px solid var(--ink)", paddingBottom:4}}>
          {[
            ["programme","Programme"],
            hasDocuments ? ["documents","Documents", documents.length] : null,
            ["artistes","Artistes", p.artistCount],
            ["lieux","Lieux", p.venueCount],
            ["parcours", "Parcours"]
          ].filter(Boolean).map(([id, label, count]) => (
            <button key={id} onClick={() => setTab(id)}
              className="row gap-2" style={{
                padding:"10px 2px 14px",
                fontFamily:"var(--font-body)",
                fontSize:12, fontWeight:700, letterSpacing:"0.1em",
                textTransform:"uppercase",
                color: tab===id ? "var(--ink)" : "var(--ink-mute)",
                borderBottom: tab===id ? "3px solid var(--ink)" : "3px solid transparent",
                marginBottom:-4
              }}>
              {["artistes","lieux","parcours"].includes(id) && !p.paid && <Icon name="lock" size={12}/>}
              {label}
              {count !== undefined && (
                <span className="mono" style={{
                  color: tab===id ? "var(--ink-mute)" : "var(--ink-faint)",
                  fontSize:10
                }}>{count}</span>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* ===== TAB CONTENT ===== */}
      {tab === "programme" && (
        <ProgramSection
          p={p}
          venues={venues}
          program={program}
          focusedVenue={focusedVenue}
          hasDocuments={hasDocuments}
          onOpenDocuments={() => setTab("documents")}
        />
      )}
      {tab === "documents" && hasDocuments && <DocumentsSection documents={documents}/>}
      {tab === "artistes" && (
        p.paid
          ? <ArtistsSection artists={artists}/>
          : <PaidFeatureNoticeSection feature="artistes"/>
      )}
      {tab === "lieux" && (
        p.paid
          ? <VenuesSection venues={venues} p={p} focusedVenue={focusedVenue}/>
          : <PaidFeatureNoticeSection feature="lieux"/>
      )}
      {tab === "parcours" && (
        p.paid
          ? <ParcoursMapSection p={p} venues={venues}/>
          : <FreeParcoursSection documents={documents} onOpenDocuments={() => setTab("documents")}/>
      )}

      {/* ===== NEXT PARCOURS ===== */}
      <NextParcoursStrip current={p}/>

    </div>
  );
};

/* ============== FREE PARCOURS TAB ==============
   Visitors get the PDF/downloads. Communes/organisers get a Mozaika ad
   selling the paid upgrade (dynamic map, visit stats, editing, …). */
const FreeParcoursSection = ({ documents = [], onOpenDocuments }) => {
  const hasDocs = documents.length > 0;
  return (
    <section className="container-w" style={{paddingTop:24, paddingBottom:56}}>
      {/* Public downloads */}
      <div style={{
        border:"2px solid var(--ink)", borderRadius:16,
        background:"var(--paper-warm)", padding:"28px 28px 24px",
        marginBottom:32
      }}>
        <div className="row between wrap" style={{gap:16, marginBottom:14}}>
          <h2 style={{fontSize:32, margin:0}}>
            <span style={{borderBottom:"4px solid var(--ink)", paddingBottom:4}}>
              Le parcours à emporter
            </span>
          </h2>
          {hasDocs && (
            <button type="button" onClick={onOpenDocuments} className="btn btn-light">
              Voir tous les documents ({documents.length})
            </button>
          )}
        </div>
        {hasDocs ? (
          <div className="row gap-3 wrap">
            {documents.slice(0, 3).map((d, i) => (
              <a key={(d.title || d.name || i) + "-doc"}
                 href={d.url || d.href}
                 target={d.target || "_blank"} rel="noreferrer"
                 className="row gap-2" style={{
                   background:"var(--ink)", color:"var(--paper)",
                   padding:"14px 20px", borderRadius:6,
                   fontFamily:"var(--font-body)", fontWeight:700, fontSize:12,
                   letterSpacing:"0.08em", textTransform:"uppercase",
                   textDecoration:"none"
                 }}>
                <Icon name="calendar" size={14}/>
                ↓ {d.title || d.name || d.type || "Document"}
              </a>
            ))}
          </div>
        ) : (
          <p className="muted" style={{fontSize:14, margin:0}}>
            Aucun document publié pour le moment. Le PDF du parcours sera
            téléchargeable ici dès qu'il sera disponible.
          </p>
        )}
      </div>

      {/* Mozaika ad — target : communes / organisers */}
      <div style={{position:"relative"}}>
        <Tape color="yellow" style={{top:-14, left:52, width:200, height:24, transform:"rotate(-4deg)", zIndex:4}}/>
        <Tape color="pink" style={{bottom:18, right:64, width:180, height:22, transform:"rotate(5deg)", zIndex:4}}/>

        <div style={{
          position:"relative",
          border:"2px solid var(--ink)", borderRadius:20,
          background:"linear-gradient(135deg, var(--paper-warm) 0%, var(--paper) 55%, var(--paper-3) 100%)",
          padding:"40px 32px", overflow:"hidden",
          boxShadow:"var(--shadow-l)"
        }}>
          <div style={{
            position:"absolute", right:-40, top:-90,
            fontFamily:"var(--font-display)", fontSize:"clamp(120px, 18vw, 240px)",
            lineHeight:0.8, color:"var(--pink)", opacity:0.15,
            transform:"rotate(-8deg)", pointerEvents:"none"
          }}>
            MOZAIKA
          </div>

          <div style={{position:"relative", zIndex:2, maxWidth:720}}>
            <span className="mono" style={{color:"var(--pink)", fontSize:12, letterSpacing:"0.12em", textTransform:"uppercase"}}>
              · Pour les communes & organisateurs ·
            </span>
            <h2 style={{fontSize:"clamp(32px, 5vw, 56px)", lineHeight:0.95, margin:"12px 0 16px"}}>
              Passez à la carte dynamique.
            </h2>
            <p style={{fontSize:16, lineHeight:1.55, color:"var(--ink-soft)", marginTop:0}}>
              Publiez votre parcours sur <strong>Mozaika</strong> et débloquez la carte
              interactive géolocalisée, le suivi des visites en temps réel, les statistiques
              d'engagement, l'édition en ligne, le partage QR, et bien plus.
            </p>

            <div className="row gap-3 wrap" style={{marginTop:16, marginBottom:20}}>
              {[
                ["Carte interactive", "map"],
                ["Statistiques de visites", "chart"],
                ["Édition en ligne", "sparkle"],
                ["Partage QR-Code", "share"]
              ].map(([t, icon]) => (
                <span key={t} className="row gap-2" style={{
                  background:"var(--paper-warm)", color:"var(--ink)",
                  border:"1.5px solid var(--ink)", borderRadius:999,
                  padding:"6px 14px", fontFamily:"var(--font-body)",
                  fontWeight:600, fontSize:12
                }}>
                  <Icon name={icon} size={12}/>
                  {t}
                </span>
              ))}
            </div>

            <div className="row gap-3 wrap">
              <a href="/publier" className="btn btn-primary">Publier mon parcours →</a>
              <a href="https://mozaika.be" target="_blank" rel="noreferrer" className="btn btn-light">
                En savoir plus sur Mozaika
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============== QR CODE MODAL ============== */
const QrCodeModal = ({ url, title, onClose }) => {
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=480x480&margin=12&data=${encodeURIComponent(url)}`;
  const downloadHref = `${qrSrc}&format=png&download=1`;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch {}
  };

  return (
    <div
      onClick={onClose}
      style={{
        position:"fixed", inset:0, zIndex:200,
        background:"rgba(15,15,15,0.6)",
        display:"flex", alignItems:"center", justifyContent:"center",
        padding:24, animation:"fade-in .15s ease"
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background:"var(--paper)", borderRadius:18,
          padding:"28px 28px 24px", maxWidth:420, width:"100%",
          border:"2px solid var(--ink)", boxShadow:"var(--shadow-l)",
          textAlign:"center", position:"relative"
        }}
      >
        <button
          type="button" onClick={onClose} aria-label="Fermer"
          style={{
            position:"absolute", top:10, right:14,
            background:"none", border:"none", fontSize:22, cursor:"pointer",
            color:"var(--ink-mute)", fontFamily:"var(--font-body)"
          }}
        >×</button>
        <div className="display" style={{fontSize:18, marginBottom:6}}>SCANNEZ POUR PARTAGER</div>
        <div className="mono muted" style={{fontSize:11, marginBottom:18}}>{title}</div>
        <img
          src={qrSrc}
          alt={`QR code vers ${url}`}
          width={320} height={320}
          style={{
            width:"min(320px, 100%)", height:"auto", display:"block", margin:"0 auto",
            background:"#fff", padding:12, borderRadius:8, border:"1px solid var(--line)"
          }}
        />
        <div
          style={{
            marginTop:16, fontSize:12, color:"var(--ink-mute)",
            wordBreak:"break-all", padding:"8px 10px",
            background:"var(--paper-warm)", border:"1px solid var(--line)", borderRadius:6
          }}
        >
          {url}
        </div>
        <div className="row gap-2" style={{justifyContent:"center", marginTop:16, flexWrap:"wrap"}}>
          <button type="button" onClick={copy} className="btn btn-light">Copier le lien</button>
          <a href={downloadHref} download={`qr-${title}.png`} className="btn btn-primary">Télécharger</a>
        </div>
      </div>
    </div>
  );
};

/* ============== ARTISTES PRÉSENTS (panneau droit du Programme) ============== */
const ArtistsPresentCard = ({ names }) => {
  const count = names.length;
  return (
    <div className="card" style={{
      padding:"18px 22px",
      background:"var(--paper-warm)",
      border:"1.5px solid var(--ink)",
      borderRadius:14,
      boxShadow:"var(--shadow-s)"
    }}>
      <div className="row between" style={{marginBottom:12}}>
        <div className="display" style={{fontSize:18}}>ARTISTES PRÉSENTS</div>
        <span className="mono" style={{
          fontSize:11, letterSpacing:"0.08em", textTransform:"uppercase",
          background:"var(--ink)", color:"var(--paper)",
          padding:"3px 10px", borderRadius:999
        }}>
          {count}
        </span>
      </div>
      {count === 0 ? (
        <p className="muted" style={{fontSize:13, margin:0}}>
          La liste des artistes n'est pas encore disponible.
        </p>
      ) : (
        <div className="row gap-2 wrap">
          {names.map((n) => (
            <span key={n} style={{
              display:"inline-flex", alignItems:"center",
              padding:"6px 12px", borderRadius:999,
              background:"var(--paper)", color:"var(--ink)",
              border:"1px solid var(--line)",
              fontSize:13, fontWeight:600
            }}>
              {n}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/* ============== PARCOURS MAP (offre payante) ============== */
const ParcoursMapSection = ({ p, venues }) => (
  <section className="container-w" style={{paddingTop:24, paddingBottom:56}}>
    <div className="row between wrap" style={{gap:16, marginBottom:24}}>
      <h2 style={{fontSize:48, margin:0}}>
        <span style={{borderBottom:"4px solid var(--ink)", paddingBottom:6}}>Carte du parcours</span>
      </h2>
      <span className="mono muted" style={{fontSize:12}}>
        {venues.length} {venues.length > 1 ? "lieux" : "lieu"} à découvrir
      </span>
    </div>
    <div style={{
      borderRadius:14, overflow:"hidden", border:"2px solid var(--ink)",
      boxShadow:"var(--shadow-l)", background:"var(--paper-2)"
    }}>
      <ParcoursOLMap p={p} venues={venues} height={520} drawZone/>
    </div>
    {venues.length === 0 && (
      <p className="muted" style={{marginTop:16, fontSize:13}}>
        Aucun lieu géolocalisé pour le moment. La carte se peuplera dès l'ajout de lieux au parcours.
      </p>
    )}
  </section>
);

/* ============== PAID FEATURE NOTICE ============== */
const PaidFeatureNoticeSection = ({ feature }) => {
  const isArtists = feature === "artistes";
  const copy = {
    artistes: {
      label: "Les artistes",
      teaser: "fiches détaillées des artistes, disciplines, portraits et liens vers leurs ateliers",
      accent: "var(--pink)",
      tape: "yellow",
      punch: "Des portraits, des histoires, des pratiques.",
      mini: ["portraits", "disciplines", "ateliers"]
    },
    lieux: {
      label: "Les lieux",
      teaser: "carte interactive des lieux, fiches par adresse, horaires et accessibilité",
      accent: "var(--green)",
      tape: "pink",
      punch: "Des espaces, des adresses, des parcours vivants.",
      mini: ["carte", "adresses", "accessibilite"]
    },
    parcours: {
      label: "La carte du parcours",
      teaser: "carte interactive avec les lieux, itinéraires et points d'intérêt géolocalisés",
      accent: "var(--orange)",
      tape: "pink",
      punch: "Une carte vivante pour préparer la visite.",
      mini: ["carte", "itinéraires", "géolocalisation"]
    }
  }[feature] || {
    label: "Cette fonctionnalité",
    teaser: "des fonctionnalités avancées",
    accent: "var(--orange)",
    tape: "yellow",
    punch: "Un mode avancé pour les editions premium.",
    mini: ["premium", "activation", "support"]
  };

  const mailto = "mailto:contact@mozaika.be?subject=" +
    encodeURIComponent("Débloquer le plein potentiel de la plateforme");

  return (
    <section className="container-w" style={{paddingTop:24, paddingBottom:56}}>
      <div style={{position:"relative"}}>
        <Tape color={copy.tape} style={{top:-14, left:52, width:220, height:24, transform:"rotate(-5deg)", zIndex:4}}/>
        <Tape color="yellow" style={{bottom:18, right:88, width:180, height:22, transform:"rotate(4deg)", zIndex:4}}/>

        <div style={{
          position:"relative",
          minHeight:"clamp(400px, 58vh, 560px)",
          border:"2px solid var(--ink)",
          borderRadius:20,
          overflow:"hidden",
          boxShadow:"var(--shadow-l)",
          background:"linear-gradient(130deg, var(--paper-warm) 0%, var(--paper) 38%, var(--paper-3) 100%)"
        }}>
          <div style={{
            position:"absolute",
            top:-90,
            right:-40,
            fontFamily:"var(--font-display)",
            fontSize:"clamp(120px, 19vw, 270px)",
            lineHeight:0.8,
            color:copy.accent,
            opacity:0.18,
            transform:"rotate(-7deg)",
            pointerEvents:"none"
          }}>
            PREMIUM
          </div>

          <div style={{
            position:"absolute",
            top:18,
            left:"50%",
            transform:"translateX(-50%) rotate(-1deg)",
            background:"var(--ink)",
            color:"var(--paper)",
            border:"2px solid var(--ink)",
            borderRadius:999,
            padding:"8px 18px",
            boxShadow:"var(--shadow-s)",
            zIndex:3
          }}>
            <span className="mono" style={{fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase"}}>
              Fonctionnalite active sur offre payante
            </span>
          </div>

          <div className="split-2" style={{
            display:"grid",
            gridTemplateColumns:"minmax(0, 1.1fr) minmax(0, 0.9fr)",
            gap:28,
            alignItems:"stretch",
            padding:"84px 34px 34px"
          }}>
            <div className="stack gap-5" style={{minWidth:0}}>
              <div style={{
                width:64,
                height:64,
                borderRadius:14,
                background:"var(--ink)",
                color:"var(--paper)",
                display:"flex",
                alignItems:"center",
                justifyContent:"center",
                boxShadow:"var(--shadow-s)"
              }}>
                <Icon name="lock" size={30}/>
              </div>

              <h2 style={{fontSize:"clamp(42px, 6vw, 88px)", lineHeight:0.9, margin:0}}>
                {copy.label}
                <br/>
                <span className="script" style={{fontSize:"0.66em", color:copy.accent}}>disponible</span>
              </h2>

              <p style={{fontSize:17, lineHeight:1.55, color:"var(--ink-soft)", margin:0, maxWidth:640}}>
                L'activation se fait sur votre edition via l'offre premium Mozaika.
              </p>

              <div className="row gap-3 wrap" style={{marginTop:8}}>
                <a href={mailto} className="btn btn-primary row gap-2" style={{padding:"14px 18px"}}>
                  <Icon name="mail" size={15}/>
                  Activer cette section
                </a>
                {/* <a href="/organization" className="btn btn-light" style={{padding:"14px 18px"}}>
                  Decouvrir l'offre
                </a> */}
              </div>
            </div>

            <div style={{
              minWidth:0,
              border:"2px dashed var(--ink)",
              borderRadius:14,
              background:"rgba(255,255,255,0.45)",
              padding:"20px 20px 18px",
              display:"grid",
              gridTemplateRows:"auto auto auto 1fr auto",
              gap:12
            }}>
              <div className="mono" style={{fontSize:11, letterSpacing:"0.1em", textTransform:"uppercase", color:"var(--ink-mute)"}}>
                Vision de la section
              </div>

              <div className="display" style={{fontSize:24, lineHeight:1.06, maxWidth:360}}>{copy.punch}</div>

              <div className="row gap-2 wrap" style={{marginBottom:2}}>
                {copy.mini.map((tag) => (
                  <span key={tag} className="mono" style={{
                    padding:"6px 10px",
                    border:"1.5px solid var(--ink)",
                    borderRadius:999,
                    fontSize:11,
                    background:"var(--paper)"
                  }}>
                    {tag}
                  </span>
                ))}
              </div>

              <div style={{
                border:"1.5px solid var(--ink)",
                borderRadius:12,
                background:"var(--paper)",
                padding:"14px 14px 12px",
                boxShadow:"var(--shadow-s)",
                minHeight:120,
                display:"grid",
                alignContent:"start",
                gap:8
              }}>
                <div className="mono" style={{fontSize:11, letterSpacing:"0.08em", textTransform:"uppercase", color:"var(--ink-mute)"}}>
                  Fonctionnalites incluses
                </div>
                <div className="stack gap-2" style={{fontSize:13, lineHeight:1.45, color:"var(--ink-soft)"}}>
                  <div>• {isArtists ? "Listing des artistes avec fiches detaillees" : "Listing des lieux avec informations pratiques"}</div>
                  <div>• {isArtists ? "Filtres par discipline et atelier" : "Filtres par commune et accessibilite"}</div>
                  <div>• {isArtists ? "Portraits, biographie et parcours associes" : "Carte, horaires et points d'interet"}</div>
                  <div>• {isArtists ? "QR Code" : "QR Code"}</div>
                </div>
              </div>

              <div style={{
                borderTop:"1px dashed var(--ink)",
                paddingTop:10,
                fontSize:13,
                lineHeight:1.45,
                color:"var(--ink-soft)"
              }}>
                Vous gardez vos onglets visibles pour le public, puis vous activez cette experience complete quand vous le souhaitez.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============== HERO ============== */
const DetailHero = ({ p }) => {
  const D = window.PA_DATA;
  const [shareOpen, setShareOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const fullDescription = (p.description || "").trim();
  const shortSummary = (p.summary || "").trim();
  // Le bouton « Lire plus » n'apparaît que si le résumé est réellement tronqué.
  const summaryHasMore = fullDescription.length > shortSummary.length;
  const shareMenuRef = useRef(null);
  const shareUrl = `${window.location.origin}/parcours/${p.slug}`;
  const shareText = `${p.title} · ${p.dates}`;
  // Sticker carré : « en un coup d'œil ». On n'affiche que ce qui existe, et à
  // défaut l'année de l'édition — jamais un carton vide.
  const stickerLines = [
    p.venueCount > 0 ? `${p.venueCount} LIEU${p.venueCount > 1 ? "X" : ""}` : null,
    p.artistCount > 0 ? `${p.artistCount} ARTISTE${p.artistCount > 1 ? "S" : ""}` : null
  ].filter(Boolean);
  const editionYear = String(p.startISO || "").slice(0, 4) || String(new Date().getFullYear());
  const stickerContent = stickerLines.length ? stickerLines : ["ÉDITION", editionYear];

  const placeLabel = (() => {
    const raw = String(p.region || p.mainCommune || "").trim();
    if (!raw) return "";
    const stripped = raw.replace(/^r[eé]gion\s+/i, "");
    return stripped.charAt(0).toUpperCase() + stripped.slice(1);
  })();

  // Adapt the title size to the longest word so it never gets cut mid-word.
  // The longer the biggest word, the smaller the type — long words then break
  // cleanly with a hyphen (see the h1 hyphenation rules below).
  const longestWord = (p.title || "").split(/\s+/).reduce((m, w) => Math.max(m, w.length), 0);
  const titleSize =
    longestWord <= 7  ? "clamp(56px, 7.2vw, 124px)" :
    longestWord <= 10 ? "clamp(44px, 6vw, 96px)"   :
    longestWord <= 13 ? "clamp(36px, 4.8vw, 74px)" :
                        "clamp(30px, 4vw, 60px)";

  // Close the share dropdown when the user clicks outside of it, or hits Escape.
  useEffect(() => {
    if (!shareOpen) return;
    const onOutside = (e) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target)) {
        setShareOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setShareOpen(false); };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [shareOpen]);

  const openSharePopup = (url) => {
    window.open(url, "_blank", "noopener,noreferrer,width=640,height=720");
  };

  const onShareClick = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: p.title,
          text: shareText,
          url: shareUrl
        });
        return;
      } catch (e) {
        // If native share is cancelled/unavailable, keep fallback menu available.
      }
    }
    setShareOpen((s) => !s);
  };

  const onCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareOpen(false);
    } catch (e) {
      window.prompt("Copiez ce lien :", shareUrl);
    }
  };

  const socialLinks = [
    {
      id: "facebook",
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    },
    {
      id: "x",
      label: "X",
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      href: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`
    }
  ];

  return (
    <section className="container-w" style={{paddingTop:24, paddingBottom:24}}>
      <div className="detail-hero-grid" style={{
        display:"grid",
        gridTemplateColumns:"minmax(0, 1fr) minmax(0, 1.05fr)",
        gap:48, alignItems:"stretch"
      }}>
        {/* LEFT — title block */}
        <div className="stack gap-6 detail-hero-copy" style={{position:"relative", paddingTop:8, paddingRight:20, minWidth:0}}>
          <StatusDot status={p.status}/>
            {p.status === "past" && (
              <div className="mono muted" style={{fontSize:12, letterSpacing:"0.08em", textTransform:"uppercase"}}>
                Ce parcours est terminé.
              </div>
            )}

          <div style={{position:"relative"}}>
            <h1 lang="fr" style={{
              fontSize: titleSize,
              letterSpacing:"-0.03em",
              lineHeight:0.88,
              textTransform:"uppercase",
              // Adapt: shrink to fit; only break long words with a real hyphen,
              // never leaving fewer than 3 letters on a line (no "bonjou-r").
              overflowWrap:"break-word",
              hyphens:"auto",
              WebkitHyphens:"auto",
              hyphenateLimitChars:"6 3 3",
              WebkitHyphenateLimitBefore:3,
              WebkitHyphenateLimitAfter:3
            }}>
              {p.title}
              <svg viewBox="0 0 600 24" preserveAspectRatio="none"
                   style={{width:"100%", height:18, marginTop:-4, display:"block"}}>
                <path d="M6 14 C100 4 220 18 360 10 C440 6 540 16 594 8"
                      fill="none" stroke="var(--ink)" strokeWidth="4" strokeLinecap="round"/>
              </svg>
            </h1>
            <AsteriskHand color="var(--ink)" size={36} style={{
              position:"absolute", right:0, top:6, transform:"rotate(8deg)"
            }}/>
          </div>

          <p className="detail-hero-summary" style={{fontSize:15.5, lineHeight:1.55, color:"var(--ink-soft)", maxWidth:420, margin:0, whiteSpace:"pre-line"}}>
            {summaryExpanded && summaryHasMore ? fullDescription : shortSummary}
            {summaryHasMore && (
              <>
                {" "}
                <button type="button"
                  onClick={() => setSummaryExpanded(s => !s)}
                  aria-expanded={summaryExpanded}
                  style={{
                    background:"none", border:"none", padding:0, cursor:"pointer",
                    font:"inherit", fontWeight:700, color:"var(--ink)",
                    textDecoration:"underline", textUnderlineOffset:3, whiteSpace:"nowrap"
                  }}>
                  {summaryExpanded ? "Lire moins" : "Lire plus"}
                </button>
              </>
            )}
          </p>

          {/* meta row */}
          <div className="row gap-6 wrap" style={{marginTop:4}}>
            <div className="row gap-2 mono">
              <Icon name="calendar" size={13}/> <span>{p.dates}</span>
            </div>
            <div className="row gap-2 mono">
              <Icon name="pin" size={13}/> <span>{p.region.toUpperCase()}</span>
            </div>
            <div className="row gap-2 mono">
              <Icon name="users" size={13}/> <span>{p.artistCount} ARTISTES</span>
            </div>
          </div>

          {/* CTAs */}
          <div className="row gap-3 wrap" style={{marginTop:8}}>
            <button type="button" onClick={() => setQrOpen(true)} className="btn btn-primary">
              Voir le Qr-Code
            </button>
            {qrOpen && <QrCodeModal url={shareUrl} title={p.title} onClose={() => setQrOpen(false)}/>}
            <div ref={shareMenuRef} style={{position:"relative"}}>
              <button onClick={onShareClick} className="btn btn-light">
                <Icon name="share" size={14}/>
                Partager
              </button>
              {shareOpen && (
                <div style={{
                  position:"absolute",
                  top:"calc(100% + 8px)",
                  left:0,
                  zIndex:20,
                  minWidth:220,
                  background:"var(--paper-warm)",
                  border:"1.5px solid var(--ink)",
                  borderRadius:8,
                  boxShadow:"var(--shadow)",
                  padding:8
                }}>
                  <div className="mono" style={{padding:"6px 8px", color:"var(--ink-mute)"}}>Partager sur</div>
                  {socialLinks.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => openSharePopup(item.href)}
                      className="row between"
                      style={{
                        width:"100%",
                        padding:"10px 8px",
                        borderRadius:6,
                        fontSize:13,
                        fontWeight:600,
                        color:"var(--ink)",
                        textAlign:"left"
                      }}
                    >
                      <span>{item.label}</span>
                      <Icon name="external" size={12}/>
                    </button>
                  ))}
                  <button
                    onClick={onCopyLink}
                    className="row between"
                    style={{
                      width:"100%",
                      padding:"10px 8px",
                      borderRadius:6,
                      fontSize:13,
                      fontWeight:600,
                      color:"var(--ink)",
                      textAlign:"left",
                      borderTop:"1px solid var(--line)",
                      marginTop:4
                    }}
                  >
                    <span>Copier le lien</span>
                    <Icon name="link" size={12}/>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT — photo + stickers */}
        <div className="detail-hero-media" style={{position:"relative", minHeight:480, minWidth:0}}>
          <div className="detail-hero-frame" style={{position:"relative"}}>
          {/* Big photo — sized normally so layout reserves room */}
          <div className="detail-hero-photo" style={{
            width:"100%", height:480,
            borderRadius:10, overflow:"hidden", border:"1.5px solid var(--ink)",
            background:"var(--paper-3)", position:"relative"
          }}>
            <img className="detail-hero-photo-img" src={p.image || D.photo(p.id + "-hero", 1800, 1400)} alt={p.title}
                 style={{width:"100%", height:"100%", objectFit:"contain", display:"block",
                         background:"var(--paper-warm)"}}
                 onError={(e)=>{e.currentTarget.style.display="none";}}/>

            {/* Dates + lieu, dans le coin haut-gauche de la photo */}
            {(p.dates || placeLabel) && (
              <div className="detail-hero-sticker" style={{
                position:"absolute", top:24, left:24, zIndex:3
              }}>
                {p.dates && (
                  <Marker color="pink" rotate={-3} style={{padding:"12px 20px", maxWidth:250}}>
                    <span className="display" style={{
                      display:"block", textAlign:"center",
                      fontSize:17, lineHeight:1.15, color:"var(--ink)"
                    }}>{p.dates.toUpperCase()}</span>
                  </Marker>
                )}
                {placeLabel && (
                  <div className="circled" style={{marginTop:10, marginLeft:22}}>
                    <span className="script" style={{fontSize:22, color:"var(--ink)"}}>{placeLabel}</span>
                  </div>
                )}
              </div>
            )}
          </div>

            {/* Carton carré en débord du bord droit — hors du cadre, sinon
                l'overflow:hidden de la photo le rognait */}
            <div className="detail-hero-card" style={{
              position:"absolute", right:-12, top:80, zIndex:4,
              background:"var(--paper-warm)", border:"1.5px solid var(--ink)",
              padding:"14px 12px",
              transform:"rotate(4deg)",
              boxShadow:"var(--shadow)"
            }}>
              <Tape color="clear" style={{top:-10, left:"50%", marginLeft:-22, width:44, height:14, transform:"rotate(0deg)"}}/>
              <div className="display" style={{fontSize:13, lineHeight:1.25, textAlign:"center"}}>
                {stickerContent.map((line, i) => <div key={i}>{line}</div>)}
              </div>
            </div>

            {/* Bottom black round explorer badge — overflows photo edge */}
            <div style={{
              position:"absolute", bottom:-28, right:24, zIndex:4
            }}>
              <ExplorerBadge/>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

/* Round black "EXPLORER LE PARCOURS" badge with rotating text — scrolls to the tabs. */
const ExplorerBadge = () => {
  const onClick = (e) => {
    e.preventDefault();
    const tabs = document.querySelector("[data-parcours-tabs]");
    if (tabs) {
      tabs.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollBy({ top: window.innerHeight * 0.7, behavior: "smooth" });
    }
  };
  return (
    <a href="#programme" onClick={onClick} className="row" aria-label="Explorer le parcours" style={{
      width:120, height:120, borderRadius:"50%",
      background:"var(--ink)", color:"var(--paper)",
      justifyContent:"center", alignItems:"center",
      position:"relative", textDecoration:"none",
      boxShadow:"var(--shadow-l)", cursor:"pointer"
    }}>
      <svg viewBox="0 0 120 120" style={{position:"absolute", inset:0, width:"100%", height:"100%"}}>
        <defs>
          <path id="circle-text" d="M 60,60 m -42,0 a 42,42 0 1,1 84,0 a 42,42 0 1,1 -84,0"/>
        </defs>
        <text fontFamily="var(--font-body)" fontSize="9" fontWeight="700" fill="var(--paper)" letterSpacing="3">
          <textPath href="#circle-text">EXPLORER LE PARCOURS · EXPLORER LE PARCOURS · </textPath>
        </text>
      </svg>
      <Icon name="arrow-up-right" size={28}/>
    </a>
  );
};

/* Rend cliquables les URLs et e-mails contenus dans un texte libre (ex : le contact). */
const LINKIFY_RE = /((?:https?:\/\/|www\.)[^\s]+|[^\s@]+@[^\s@]+\.[a-zA-Z]{2,})/g;
const linkify = (text) => String(text).split(LINKIFY_RE).map((part, i) => {
  if (i % 2 === 0 || !part) return part;
  // Ne pas inclure la ponctuation finale dans le lien ("voir www.site.be." → "www.site.be")
  const m = part.match(/[.,;:!?)]+$/);
  const trail = m ? m[0] : "";
  const core = trail ? part.slice(0, -trail.length) : part;
  const isEmail = core.includes("@") && !/^(https?:\/\/|www\.)/i.test(core);
  const href = isEmail ? `mailto:${core}` : (/^www\./i.test(core) ? `https://${core}` : core);
  return (
    <React.Fragment key={i}>
      <a href={href} target={isEmail ? undefined : "_blank"} rel="noopener noreferrer"
         style={{color:"inherit", fontWeight:600, textDecoration:"underline", wordBreak:"break-all"}}>
        {core}
      </a>{trail}
    </React.Fragment>
  );
});

/* Formatage FR des dates ISO du programme ("2026-09-27" → libellés lisibles). */
const MONTHS_FR = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
const WEEKDAYS_FR = ["dimanche","lundi","mardi","mercredi","jeudi","vendredi","samedi"];
const parseISODate = (iso) => {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  return y && m && d ? { y, m, d, wd: new Date(Date.UTC(y, m - 1, d)).getUTCDay() } : null;
};
/* "Sam. 27/09" — pour les onglets de jour. */
const fmtDayChip = (iso) => {
  const p = parseISODate(iso);
  if (!p) return null;
  const wd = WEEKDAYS_FR[p.wd].slice(0, 3);
  return `${wd.charAt(0).toUpperCase() + wd.slice(1)}. ${String(p.d).padStart(2, "0")}/${String(p.m).padStart(2, "0")}`;
};
/* "Samedi 27 septembre 2026" — date complète du jour sélectionné. */
const fmtDayFull = (iso) => {
  const p = parseISODate(iso);
  if (!p) return null;
  const wd = WEEKDAYS_FR[p.wd];
  return `${wd.charAt(0).toUpperCase() + wd.slice(1)} ${p.d} ${MONTHS_FR[p.m - 1]} ${p.y}`;
};
/* "dimanche 28 septembre" — pour la mention "Jusqu'au …" des programmes multi-jours. */
const fmtDayMedium = (iso) => {
  const p = parseISODate(iso);
  return p ? `${WEEKDAYS_FR[p.wd]} ${p.d} ${MONTHS_FR[p.m - 1]}` : String(iso);
};

/* ============== PROGRAMME SECTION ============== */
const ProgramSection = ({ p, venues, program, focusedVenue, hasDocuments, onOpenDocuments }) => {
  const [day, setDay] = useState(0);
  const safeProgram = Array.isArray(program) ? program : [];
  const selectedDay = safeProgram[day] || null;
  const practicalAccess = p.horaires || "Consultez les horaires du parcours pour préparer votre visite.";
  const practicalAccessibility = p.accessibility || "Voir le détail des lieux pour les informations d'accessibilité.";
  const practicalContact = [p.contact, p.secondaryContact, p.website].filter(Boolean).join("\n");
  const descriptionMode = p.contentMode === "DESCRIPTION";
  // Heure de fin du jour sélectionné : la plus tardive des heures de fin (ou de début à défaut)
  // des événements du jour. Ceux qui débordent sur un autre jour (endDate) ne comptent pas.
  const dayEndTime = (() => {
    const events = Array.isArray(selectedDay?.events) ? selectedDay.events : [];
    const times = events
      .filter(e => !e.endDate)
      .map(e => e.endTime || e.time)
      .filter(Boolean);
    return times.length ? times.reduce((a, b) => (a > b ? a : b)) : null;
  })();
  return (
    <section className="container-w" style={{paddingTop:24, paddingBottom:48}}>
      <div className="row" style={{gap:48, alignItems:"flex-start", flexWrap:"wrap"}}>
        {/* LEFT — Programme or long description */}
        <div className="stack gap-5" style={{flex:"1 1 560px", minWidth:0}}>
          <div className="between">
            <div className="stack gap-2">
              <h2 style={{fontSize:48}}>
                <span style={{borderBottom:"4px solid var(--ink)", paddingBottom:6, display:"inline-block"}}>
                  {descriptionMode ? "À propos" : "Le programme"}
                </span>
              </h2>
              {!descriptionMode && selectedDay?.date && (
                <div className="mono muted" style={{fontSize:12, letterSpacing:"0.08em", textTransform:"uppercase", marginTop:24}}>
                  {fmtDayFull(selectedDay.date)}
                </div>
              )}
            </div>
            {!descriptionMode && (
              <div className="row gap-2">
                {safeProgram.map((s, i) => (
                  <button key={i} onClick={() => setDay(i)}
                    className={"chip " + (day===i?"chip-active":"")}
                    style={{padding:"6px 10px"}}>{fmtDayChip(s.date) || `J${i+1}`}</button>
                ))}
              </div>
            )}
          </div>

          <div style={{marginTop:16}}>
            {descriptionMode
              ? <LongDescription text={p.longDescription}/>
              : <ProgramList day={selectedDay}/>}
          </div>

          {!descriptionMode && selectedDay && dayEndTime && (
            <div className="row" style={{
              background:"var(--ink)", color:"var(--paper)",
              padding:"14px 22px", borderRadius:8, gap:20,
              marginTop:4
            }}>
              <span className="display" style={{fontSize:18}}>{dayEndTime}</span>
              <Icon name="dot" size={6} style={{color:"var(--paper-3)"}}/>
              <span className="mono" style={{color:"var(--paper-3)"}}>Fin du parcours</span>
            </div>
          )}
        </div>

        {/* RIGHT — Artistes présents + infos pratiques */}
        <div className="stack gap-5" style={{flex:"1 1 420px", minWidth:0}}>
          <ArtistsPresentCard names={p.artistNames || []}/>

          <div className="row" style={{gap:20, alignItems:"flex-start", flexWrap:"wrap"}}>
            <Sticky color="purple" rotate={-1} style={{flex:"1 1 300px"}}>
              <div className="display" style={{fontSize:18, marginBottom:18}}>INFOS PRATIQUES</div>
              <div className="stack gap-4">
                {[
                  ["calendar","Dates", practicalAccess],
                  ["users","Accessibilité", practicalAccessibility],
                  ["share","Contact", practicalContact || "Les coordonnées de contact seront bientôt disponibles."]
                ].map(([icon, k, v]) => (
                  <div key={k} className="row gap-3" style={{alignItems:"flex-start"}}>
                    <div style={{
                      width:30, height:30, borderRadius:6, background:"var(--ink)", color:"var(--paper)",
                      display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0
                    }}><Icon name={icon} size={14}/></div>
                    <div className="stack gap-1" style={{flex:1, minWidth:0}}>
                      <div className="display" style={{fontSize:12}}>{k.toUpperCase()}</div>
                      <div style={{fontSize:13, lineHeight:1.5, whiteSpace:"pre-line"}}>{linkify(v)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Sticky>

            <div className="stack gap-3" style={{flex:"1 1 180px"}}>
              {hasDocuments && (
                <a href="#" onClick={(e) => {
                  e.preventDefault();
                  onOpenDocuments?.();
                }} className="row gap-2" style={{
                  background:"var(--ink)", color:"var(--paper)",
                  padding:"16px 22px", borderRadius:4,
                  fontFamily:"var(--font-body)", fontWeight:700, fontSize:11,
                  letterSpacing:"0.1em", textTransform:"uppercase"
                }}>
                  ↓ Voir les Documents
                </a>
              )}
              <Sticky color="yellow" rotate={2} style={{padding:22, position:"relative"}}>
                <div className="display" style={{fontSize:14, lineHeight:1.2}}>
                  TOUS LES LIEUX OUVRENT LEURS PORTES. VENEZ EXPLORER !
                </div>
                <AsteriskHand color="var(--ink)" size={28} style={{
                  position:"absolute", right:-8, bottom:-8, transform:"rotate(20deg)"
                }}/>
              </Sticky>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ProgramList = ({ day }) => {
  const events = Array.isArray(day?.events) ? day.events : [];

  if (!day || events.length === 0) {
    return (
      <div className="card" style={{padding:"18px 20px", background:"var(--paper-2)"}}>
        <div className="mono muted">Programme à venir.</div>
      </div>
    );
  }

  return (
    <div className="stack" style={{position:"relative"}}>
      <div style={{position:"absolute", left:96, top:0, bottom:0, width:2, background:"var(--ink)", opacity:0.15}}/>
      {events.map((e,i) => <ProgramRow key={i} e={e} idx={i}/>)}
    </div>
  );
};

const LongDescription = ({ text }) => {
  const clean = (text || "").trim();
  if (!clean) {
    return (
      <div className="card" style={{padding:"18px 20px", background:"var(--paper-2)"}}>
        <div className="mono muted">Description à venir.</div>
      </div>
    );
  }
  // Split on blank lines so each paragraph gets its own <p>; single line-breaks preserved via whiteSpace.
  const paragraphs = clean.split(/\n\s*\n+/);
  return (
    <div className="stack gap-4" style={{fontSize:16, lineHeight:1.65, color:"var(--ink)"}}>
      {paragraphs.map((para, i) => (
        <p key={i} style={{margin:0, whiteSpace:"pre-line"}}>{linkify(para)}</p>
      ))}
    </div>
  );
};

const ProgramRow = ({ e, idx }) => {
  const tagColors = {
    vernissage: {bg:"var(--pink)",    fg:"var(--paper)", lbl:"OUVERTURE"},
    performance:{bg:"var(--purple-d)",fg:"var(--paper)", lbl:"PERFORMANCE"},
    convivial:  {bg:"var(--green)",   fg:"var(--ink)",   lbl:"CONVIVIAL"},
    "démo":     {bg:"var(--yellow)",  fg:"var(--ink)",   lbl:"DÉMONSTRATION"},
    atelier:    {bg:"var(--blue)",    fg:"var(--paper)", lbl:"ATELIER"},
    talk:       {bg:"var(--purple)",  fg:"var(--ink)",   lbl:"CONVERSATION"},
    "marché":   {bg:"var(--orange)",  fg:"var(--ink)",   lbl:"MARCHÉ"},
    libre:      {bg:"var(--yellow)",  fg:"var(--ink)",   lbl:"OUVERTURE"},
    concert:    {bg:"var(--orange)",  fg:"var(--ink)",   lbl:"CONCERT"},
    visite:     {bg:"var(--pink)",    fg:"var(--paper)", lbl:"VISITE GUIDÉE"}
  };
  const icons = {
    vernissage:"house", performance:"play", convivial:"users",
    "démo":"sparkle", atelier:"users", talk:"globe", "marché":"pin",
    libre:"compass", concert:"play", visite:"eye"
  };
  const tc = tagColors[e.tag] || tagColors.atelier;
  const hasImage = !!e.image;

  return (
    <div className="row" style={{gap:24, padding:"22px 0", borderBottom:"1px solid var(--line)"}}>
      <div style={{width:80, flexShrink:0}}>
        <div className="display" style={{fontSize:30, lineHeight:1}}>{e.time.split(" ")[0]}</div>
        {e.endTime && !e.endDate && (
          <div className="mono muted" style={{fontSize:11, marginTop:4, whiteSpace:"nowrap"}}>
            fin {e.endTime}
          </div>
        )}
      </div>
      <div style={{
        width:48, height:48, borderRadius:6,
        background: tc.bg, color: tc.fg,
        display:"flex", alignItems:"center", justifyContent:"center",
        flexShrink:0, boxShadow:"var(--shadow-s)"
      }}>
        <Icon name={icons[e.tag] || "calendar"} size={20}/>
      </div>
      <div className="stack gap-2" style={{flex:1, minWidth:0}}>
        <span className="tag-block" style={{background: tc.bg, color: tc.fg}}>{tc.lbl}</span>
        <div className="display" style={{fontSize:22, lineHeight:1.05}}>{e.title}</div>
        <div className="mono muted">{e.where.toUpperCase()}</div>
        {e.endDate && (
          <div className="mono" style={{fontSize:12, fontWeight:600}}>
            Jusqu'au {fmtDayMedium(e.endDate)}{e.endTime ? ` · ${e.endTime}` : ""}
          </div>
        )}
        {e.description && (
          <div className="muted" style={{fontSize:13, lineHeight:1.45, marginTop:2}}>
            {e.description}
          </div>
        )}
      </div>
      {hasImage && (
        <div style={{
          width:160, height:110, borderRadius:6, overflow:"hidden",
          background:"var(--paper-3)", flexShrink:0, position:"relative",
          border:"1px solid var(--line)"
        }}>
          <img src={e.image} alt=""
               style={{width:"100%", height:"100%", objectFit:"cover"}}
               onError={(e)=>{e.currentTarget.style.display="none";}}/>
        </div>
      )}
    </div>
  );
};

/* ============== ParcoursOLMap — zoomable + click popup ============== */
const ParcoursOLMap = ({ p, venues, height=380, focusedVenue, onSelect, showCommuneOnly=false, drawZone=false }) => {
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const mapInstance = useRef(null);
  const [selected, setSelected] = useState(focusedVenue || null);
  const [popupPos, setPopupPos] = useState(null);
  const colors = ["#FF3E8F","#7A5BFF","#FF7A2A","#F2E94A","#4BD18A","#4DB1FF","#C8B8FF","#E5453A"];
  const communeLabel = (p.adminCommunes && p.adminCommunes.length)
    ? p.adminCommunes.join(", ")
    : ((p.communes && p.communes.length) ? p.communes.join(", ") : p.region);

  useEffect(() => {
    if (!mapRef.current || !window.ol || mapInstance.current) return;

    // Convex hull (Andrew monotone chain) sur des points planaires [x,y] (EPSG:3857).
    const cross = (o,a,b) => (a[0]-o[0])*(b[1]-o[1]) - (a[1]-o[1])*(b[0]-o[0]);
    const convexHull = (points) => {
      const s = points.slice().sort((a,b) => a[0]-b[0] || a[1]-b[1]);
      if (s.length < 3) return s;
      const lower = [];
      for (const q of s) {
        while (lower.length >= 2 && cross(lower[lower.length-2], lower[lower.length-1], q) <= 0) lower.pop();
        lower.push(q);
      }
      const upper = [];
      for (let i = s.length-1; i >= 0; i--) {
        const q = s[i];
        while (upper.length >= 2 && cross(upper[upper.length-2], upper[upper.length-1], q) <= 0) upper.pop();
        upper.push(q);
      }
      lower.pop(); upper.pop();
      return lower.concat(upper);
    };

    const makeVenueFeature = (v, i, lonlat) => new ol.Feature({
      geometry: new ol.geom.Point(ol.proj.fromLonLat(lonlat)),
      venue: v,
      idx: i+1,
      color: colors[i % colors.length]
    });

    // On ne crée un pin que pour les lieux géolocalisés ; les lieux sans
    // coordonnées seront géocodés depuis leur adresse (Belgique) juste après.
    const vs = new ol.source.Vector({
      features: venues
        .map((v,i) => Array.isArray(v.lonlat) ? makeVenueFeature(v, i, v.lonlat) : null)
        .filter(Boolean)
    });
    const clusterSource = new ol.source.Cluster({
      distance: 44,
      minDistance: 28,
      source: vs
    });
    const styleCache = {};
    const layer = new ol.layer.Vector({
      source: clusterSource,
      style: (f) => {
        const feats = f.get("features");
        if (feats.length === 1) {
          const inner = feats[0];
          return [
            new ol.style.Style({
              image: new ol.style.Circle({
                radius: 16,
                fill: new ol.style.Fill({ color: inner.get("color") }),
                stroke: new ol.style.Stroke({ color: "#0F0F0F", width: 2 })
              })
            }),
            new ol.style.Style({
              text: new ol.style.Text({
                text: String(inner.get("idx")),
                font: "700 13px Inter, sans-serif",
                fill: new ol.style.Fill({ color: "#0F0F0F" })
              })
            })
          ];
        }
        const size = feats.length;
        const key = "c-" + size;
        if (!styleCache[key]) {
          const r = Math.min(32, 18 + Math.log2(size) * 5);
          styleCache[key] = [
            new ol.style.Style({
              image: new ol.style.Circle({
                radius: r + 5,
                fill: new ol.style.Fill({ color: "rgba(255, 62, 143, 0.22)" })
              })
            }),
            new ol.style.Style({
              image: new ol.style.Circle({
                radius: r,
                fill: new ol.style.Fill({ color: "#FF3E8F" }),
                stroke: new ol.style.Stroke({ color: "#0F0F0F", width: 2 })
              }),
              text: new ol.style.Text({
                text: String(size),
                font: "900 15px 'Archivo Black', sans-serif",
                fill: new ol.style.Fill({ color: "#0F0F0F" })
              })
            })
          ];
        }
        return styleCache[key];
      }
    });
    const map = new ol.Map({
      target: mapRef.current,
      controls: ol.control.defaults.defaults({ attribution:true, zoom:true }),
      interactions: ol.interaction.defaults.defaults({ mouseWheelZoom:true }),
      layers: [
        window.baseTileLayer({ opacity: 0.9 }),
        layer
      ],
      view: new ol.View({
        center: ol.proj.fromLonLat([4.55, 50.66]),
        zoom: 10
      })
    });
    if (vs.getFeatures().length > 0) {
      map.getView().fit(vs.getExtent(), { padding:[40,40,40,40], maxZoom:11 });
    }

    // Zone dessinée : enveloppe convexe (dilatée) autour des lieux placés.
    // Redessinable pour se mettre à jour après le géocodage asynchrone.
    let zoneLayerRef = null;
    const zoneStyle = new ol.style.Style({
      fill: new ol.style.Fill({ color: "rgba(255, 62, 143, 0.10)" }),
      stroke: new ol.style.Stroke({ color: "#0F0F0F", width: 2, lineDash: [6,4] })
    });
    const drawZoneGeom = (geometry) => {
      if (zoneLayerRef) { map.removeLayer(zoneLayerRef); zoneLayerRef = null; }
      zoneLayerRef = new ol.layer.Vector({
        source: new ol.source.Vector({ features: [new ol.Feature({ geometry })] }),
        style: zoneStyle
      });
      map.getLayers().insertAt(map.getLayers().getLength() - 1, zoneLayerRef);
    };
    const redrawZone = () => {
      if (!drawZone) return;
      const coords = vs.getFeatures().map(f => f.getGeometry().getCoordinates());
      const uniq = [...new Map(coords.map(c => [Math.round(c[0])+","+Math.round(c[1]), c])).values()];
      if (uniq.length >= 3) {
        // Plusieurs lieux : enveloppe convexe dilatée.
        const hull = convexHull(uniq);
        if (hull.length < 3) return;
        const cx = hull.reduce((s,q)=>s+q[0],0) / hull.length;
        const cy = hull.reduce((s,q)=>s+q[1],0) / hull.length;
        const ring = hull.map(([x,y]) => [cx + (x-cx)*1.12, cy + (y-cy)*1.12]);
        ring.push(ring[0]);
        drawZoneGeom(new ol.geom.Polygon([ring]));
      } else if (uniq.length >= 1) {
        // 1 ou 2 lieux : cercle-tampon autour du centre (repli 100% client).
        const cx = uniq.reduce((s,q)=>s+q[0],0) / uniq.length;
        const cy = uniq.reduce((s,q)=>s+q[1],0) / uniq.length;
        // ~1,8 km réel (coord. EPSG:3857 dilatées ~1/cos(lat)).
        drawZoneGeom(new ol.geom.Circle([cx, cy], 1800));
      }
    };
    redrawZone();

    map.on("click", (evt) => {
      const f = map.forEachFeatureAtPixel(evt.pixel, (feat) => feat);
      if (f) {
        const feats = f.get("features");
        if (feats && feats.length > 1) {
          const ext = ol.extent.createEmpty();
          feats.forEach(ff => ol.extent.extend(ext, ff.getGeometry().getExtent()));
          map.getView().fit(ext, { padding:[50,50,50,50], duration:400, maxZoom: 16 });
          return;
        }
        const single = feats ? feats[0] : f;
        const v = single.get("venue");
        if (!v) return;
        setSelected(v);
        if (onSelect) onSelect(v);
        const px = map.getPixelFromCoordinate(single.getGeometry().getCoordinates());
        setPopupPos({ x: px[0], y: px[1] });
      } else {
        setSelected(null);
        setPopupPos(null);
      }
    });
    map.on("pointermove", (evt) => {
      const hit = map.hasFeatureAtPixel(evt.pixel);
      map.getTargetElement().style.cursor = hit ? "pointer" : "";
    });
    map.on("postrender", () => {
      if (selected) {
        const f = vs.getFeatures().find(x => x.get("venue").id === selected.id);
        if (f) {
          const px = map.getPixelFromCoordinate(f.getGeometry().getCoordinates());
          setPopupPos({ x: px[0], y: px[1] });
        }
      }
    });

    mapInstance.current = map;

    // Boundary overlay for this parcours' admin communes
    if (p.adminCommunes && p.adminCommunes.length) {
      window.addCommuneBoundaryLayer(map, p.adminCommunes, {
        fill: "rgba(255, 62, 143, 0.08)",
        stroke: "#0F0F0F",
        width: 2,
        dash: [6, 4],
        fit: true,
        maxZoom: 12,
        padding: [56, 40, 40, 40]
      });
    }

    // Lieux sans coordonnées : géocodage depuis l'adresse (rue, code postal,
    // ville) restreint à la Belgique, puis ajout du pin + recadrage + zone.
    const missing = venues
      .map((v, i) => ({ v, i }))
      .filter((x) => !Array.isArray(x.v.lonlat));
    if (missing.length && typeof window.fetchAddressLonLat === "function") {
      missing.forEach((x, k) => {
        // Décalage ~1,2 s : respecte la limite de débit de Nominatim.
        setTimeout(async () => {
          if (!mapInstance.current) return;
          const ll = await window.fetchAddressLonLat({
            street: x.v.street,
            postcode: x.v.postcode,
            city: x.v.commune,
          });
          if (!ll || !mapInstance.current) return;
          x.v.lonlat = ll;
          vs.addFeature(makeVenueFeature(x.v, x.i, ll));
          map.getView().fit(vs.getExtent(), { padding: [48, 48, 48, 48], maxZoom: 14, duration: 400 });
          redrawZone();
        }, k * 1200);
      });
    }

    return () => { map.setTarget(undefined); mapInstance.current = null; };
  }, [p.id]);

  // External focus — centre la vue sur un lieu (bouton "Sur la carte").
  useEffect(() => {
    if (!focusedVenue || !mapInstance.current) return;
    setSelected(focusedVenue);
    // Le lieu peut ne pas être encore géocodé : on attend un peu et on relit
    // ses coordonnées (mises à jour sur l'objet venue après géocodage).
    let tries = 0;
    const focus = () => {
      const ll = focusedVenue.lonlat;
      if (Array.isArray(ll) && Number.isFinite(ll[0]) && Number.isFinite(ll[1])) {
        mapInstance.current.getView().animate({
          center: ol.proj.fromLonLat(ll),
          zoom: 18,
          duration: 600
        });
      } else if (tries++ < 12) {
        setTimeout(focus, 400);
      }
    };
    focus();
  }, [focusedVenue]);

  return (
    <div id="parcours-map" className="card relative" style={{
      padding:0, borderRadius:12, border:"1.5px solid var(--ink)", overflow:"hidden",
      height, background:"var(--paper-warm)"
    }}>
      <div className="row between" style={{
        padding:"14px 18px", borderBottom:"1.5px solid var(--ink)", background:"var(--paper-warm)"
      }}>
        <h3 style={{fontSize:24, lineHeight:1}}>
          <span style={{borderBottom:"3px solid var(--ink)", paddingBottom:2}}>Le parcours</span>
        </h3>
        <div className="mono" style={{fontSize:11}}>Commune: {communeLabel}</div>
      </div>
      <div ref={mapRef} className="ol-map" style={{width:"100%", height:`calc(100% - 51px)`, position:"relative"}}/>
      {/* Click popup */}
      {selected && popupPos && !showCommuneOnly && (
        <div style={{
          position:"absolute",
          left: popupPos.x, top: popupPos.y + 51 - 12,
          transform:"translate(-50%, -100%)",
          background:"var(--paper-warm)",
          border:"1.5px solid var(--ink)",
          borderRadius:8,
          padding:"12px 16px", minWidth:200, maxWidth:280,
          boxShadow:"var(--shadow-l)",
          zIndex:10
        }}>
          <div className="display" style={{fontSize:14, lineHeight:1.1, marginBottom:6}}>{selected.name}</div>
          <div className="mono muted" style={{marginBottom:8}}>{selected.type} · {selected.commune}</div>
          <div style={{fontSize:12, lineHeight:1.4, color:"var(--ink-soft)"}}>
            Ouvert pendant toute la durée du parcours.
          </div>
          {/* speech-bubble arrow */}
          <div style={{
            position:"absolute", left:"50%", bottom:-10, transform:"translateX(-50%)",
            width:0, height:0,
            borderLeft:"10px solid transparent",
            borderRight:"10px solid transparent",
            borderTop:"10px solid var(--ink)"
          }}/>
          <div style={{
            position:"absolute", left:"50%", bottom:-8, transform:"translateX(-50%)",
            width:0, height:0,
            borderLeft:"9px solid transparent",
            borderRight:"9px solid transparent",
            borderTop:"9px solid var(--paper-warm)"
          }}/>
        </div>
      )}
    </div>
  );
};

/* ============== VENUES tab ============== */
const VenuesSection = ({ venues, p, focusedVenue }) => {
  return (
    <section className="container-w" style={{paddingTop:32, paddingBottom:32}}>
      <div className="row between" style={{marginBottom:32}}>
        <h2 style={{fontSize:48}}>
          <span style={{borderBottom:"4px solid var(--ink)", paddingBottom:6}}>Les lieux</span>
        </h2>
        <span className="mono muted">{venues.length} lieu{venues.length>1?"x":""}</span>
      </div>
      <ParcoursOLMap p={p} venues={venues} height={460} focusedVenue={focusedVenue}/>
      <div style={{display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:28, marginTop:36}}>
        {venues.map(v => <VenueCard key={v.id} v={v}/>)}
      </div>
    </section>
  );
};

/* ============== ARTISTS tab ============== */
const ArtistsSection = ({ artists }) => {
  const D = window.PA_DATA;
  const list = artists.length ? artists : D.artists.slice(0, 10);
  return (
    <section className="container-w" style={{paddingTop:32, paddingBottom:32}}>
      <div className="row between" style={{marginBottom:32}}>
        <h2 style={{fontSize:48}}>
          <span style={{borderBottom:"4px solid var(--ink)", paddingBottom:6}}>Les artistes</span>
        </h2>
        <span className="mono muted">{list.length} artiste{list.length>1?"s":""}</span>
      </div>
      <div style={{display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:20}}>
        {list.map((a, i) => <ArtistCard key={a.id + i} a={a} size="md"/>)}
      </div>
    </section>
  );
};

const DocumentsSection = ({ documents }) => {
  if (!Array.isArray(documents) || documents.length === 0) return null;

  const colors = ["var(--pink)", "var(--yellow)", "var(--green)", "var(--purple)"];
  const normalized = documents
    .filter((d) => d && (d.url || d.href) && (d.title || d.name))
    .map((d) => ({
      title: d.title || d.name,
      meta: d.meta || d.label || d.type || "Document",
      href: d.url || d.href,
      download: !!d.download,
      target: d.target || "_blank"
    }));

  if (normalized.length === 0) return null;

  return (
    <section className="container-w" style={{paddingTop:32, paddingBottom:64}}>
      <h2 style={{fontSize:48, marginBottom:32}}>
        <span style={{borderBottom:"4px solid var(--ink)", paddingBottom:6}}>Documents</span>
      </h2>
      <div style={{display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:24}}>
        {normalized.map((doc, i) => (
          <a key={doc.title + i} href={doc.href} target={doc.target}
             rel={doc.target === "_blank" ? "noreferrer" : undefined}
             download={doc.download ? "" : undefined}
             className="card stack gap-3" style={{
               padding:24, border:"1.5px solid var(--ink)", background:"var(--paper-warm)"
             }}>
            <div style={{
              width:48, height:48, borderRadius:8,
              background: colors[i % colors.length],
              display:"flex", alignItems:"center", justifyContent:"center"
            }}><Icon name="external" size={20}/></div>
            <div className="display" style={{fontSize:18, lineHeight:1.05}}>{doc.title}</div>
            <div className="mono muted">{doc.meta}</div>
            <button className="btn-pill" style={{marginTop:8, alignSelf:"flex-end"}}>
              <Icon name="arrow-up-right" size={14}/>
            </button>
          </a>
        ))}
      </div>
    </section>
  );
};

/* ============== NEXT PARCOURS strip ============== */
const NextParcoursStrip = ({ current }) => {
  const D = window.PA_DATA;
  const next = D.parcours.find(p => p.id !== current.id) || D.parcours[0];
  if (!next || next.id === current.id) {
    // No other parcours — show a CTA instead
    return (
      <section style={{
        background:"var(--ink)", color:"var(--paper)", marginTop:64,
        borderTop:"3px solid var(--ink)", borderBottom:"3px solid var(--ink)"
      }}>
        <div className="container-w row between wrap" style={{padding:"32px 40px", gap:32}}>
          <div className="stack gap-2" style={{flex:1, minWidth:0}}>
            <span className="mono" style={{color:"var(--pink)"}}>· Édition 2026 ·</span>
            <div className="display" style={{fontSize:36, lineHeight:1, color:"var(--paper)"}}>
              ENVIE D'ORGANISER VOTRE PARCOURS&nbsp;?
            </div>
            <span className="mono" style={{color:"var(--paper-3)"}}>Rejoignez la plateforme et créez votre édition.</span>
          </div>
          {/* <a href="/organization" className="row gap-3" style={{
            padding:"14px 22px", background:"var(--paper)", color:"var(--ink)",
            borderRadius:999, fontWeight:700, fontSize:12,
            letterSpacing:"0.08em", textTransform:"uppercase"
          }}>
            Devenir organisateur →
          </a> */}
        </div>
      </section>
    );
  }
  return (
    <section style={{
      background:"var(--ink)", color:"var(--paper)", marginTop:64,
      borderTop:"3px solid var(--ink)", borderBottom:"3px solid var(--ink)"
    }}>
      <div className="container-w row between wrap" style={{padding:"32px 40px", gap:32}}>
        <div className="row gap-6 wrap" style={{flex:1}}>
          <div className="stack gap-1">
            <span className="mono" style={{color:"var(--paper-3)"}}>Prochain parcours</span>
            <div className="display" style={{fontSize:36, lineHeight:1, color:"var(--paper)"}}>
              {next.title.toUpperCase()}
            </div>
            <span className="mono" style={{color:"var(--paper-3)"}}>{next.dates} · {next.region}</span>
          </div>
          <div style={{
            width:160, height:108, borderRadius:6, overflow:"hidden",
            border:"1.5px solid var(--paper)", flexShrink:0
          }}>
            <img src={next.image || D.photo(next.id, 600, 400)} alt=""
                 style={{width:"100%", height:"100%", objectFit:"cover"}}/>
          </div>
        </div>
        <a href={`/parcours/${next.slug}`} className="row gap-3" style={{
          padding:"14px 22px", background:"var(--paper)", color:"var(--ink)",
          borderRadius:999, fontWeight:700, fontSize:12,
          letterSpacing:"0.08em", textTransform:"uppercase"
        }}>
          Voir le parcours →
        </a>
      </div>
    </section>
  );
};

window.ParcoursDetailPage = ParcoursDetailPage;
